// ---------------------------------------------------------------------------
// editorKit/treeOps — utilitaires génériques de manipulation d'arbre.
//
// Extrait de PageBuilder.jsx (updateTree/removeFromTree/findInTree/
// findParentInTree/insertIntoTree/moveInTree existaient déjà, inchangés ici)
// et complété avec les opérations nécessaires au reparenting (déplacer un
// élément dans un autre conteneur) : moveNodeToParent, isDescendant,
// getNodeDepth, countNodes, getMaxDepth.
//
// Fonctions pures, sans dépendance à React ni au modèle de données précis
// d'un éditeur — un nœud est simplement `{ id, children?: [...] }`.
// Réutilisable par pageBuilder, HowWeDoItEditor et emailMarketingEditor.
// ---------------------------------------------------------------------------

// Sentinelle utilisée comme "parentId" pour désigner la racine de l'arbre
// (le tableau de premier niveau) plutôt qu'un conteneur précis.
export const ROOT_PARENT_ID = "__root__";

// Recherche / remplace un nœud par id, en profondeur.
export function updateTree(nodes, id, updater) {
  return nodes.map((n) => {
    if (n.id === id) return updater(n);
    if (n.children) return { ...n, children: updateTree(n.children, id, updater) };
    return n;
  });
}

// Retire un nœud (par id) de l'arbre, où qu'il soit imbriqué.
export function removeFromTree(nodes, id) {
  return nodes
    .filter((n) => n.id !== id)
    .map((n) => (n.children ? { ...n, children: removeFromTree(n.children, id) } : n));
}

// Trouve un nœud par id, en profondeur.
export function findInTree(nodes, id) {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children) {
      const f = findInTree(n.children, id);
      if (f) return f;
    }
  }
  return null;
}

// Trouve le parent direct d'un nœud (ou null s'il est à la racine, undefined
// si l'id n'existe pas dans l'arbre).
export function findParentInTree(nodes, childId, candidate = null) {
  for (const n of nodes) {
    if (n.id === childId) return candidate;
    if (n.children) {
      const found = findParentInTree(n.children, childId, n);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

// Insère un élément à la fin des enfants d'un conteneur donné.
export function insertIntoTree(nodes, parentId, element) {
  return nodes.map((n) => {
    if (n.id === parentId && n.children) return { ...n, children: [...n.children, element] };
    if (n.children) return { ...n, children: insertIntoTree(n.children, parentId, element) };
    return n;
  });
}

// Échange un nœud avec son voisin immédiat (dir=-1 monte, dir=+1 descend),
// au même niveau de l'arbre uniquement (ne change pas de parent).
export function moveInTree(nodes, id, dir) {
  const idx = nodes.findIndex((n) => n.id === id);
  if (idx !== -1) {
    const t = idx + dir;
    if (t < 0 || t >= nodes.length) return nodes;
    const copy = [...nodes];
    [copy[idx], copy[t]] = [copy[t], copy[idx]];
    return copy;
  }
  return nodes.map((n) => (n.children ? { ...n, children: moveInTree(n.children, id, dir) } : n));
}

// Vrai si `targetId` est `ancestorId` lui-même ou se trouve dans son
// sous-arbre. Sert de garde-fou anti-cycle : un nœud ne peut jamais devenir
// son propre parent, ni être déposé dans l'un de ses descendants.
export function isDescendant(nodes, ancestorId, targetId) {
  if (ancestorId === targetId) return true;
  const ancestor = findInTree(nodes, ancestorId);
  if (!ancestor || !Array.isArray(ancestor.children)) return false;
  const walk = (list) =>
    list.some((n) => n.id === targetId || (Array.isArray(n.children) && walk(n.children)));
  return walk(ancestor.children);
}

// Retire un nœud de l'arbre et le renvoie avec le reste de l'arbre, pour
// pouvoir le réinsérer ailleurs (moveNodeToParent) sans le recréer/perdre
// ses propriétés (id, style, children…).
function extractNode(nodes, id) {
  let removed = null;
  const next = [];
  for (const n of nodes) {
    if (n.id === id) {
      removed = n;
      continue;
    }
    if (Array.isArray(n.children) && n.children.length) {
      const child = extractNode(n.children, id);
      if (child.removed) removed = child.removed;
      next.push({ ...n, children: child.nodes });
    } else {
      next.push(n);
    }
  }
  return { nodes: next, removed };
}

// Insère `node` dans les enfants de `parentId` à l'index donné (ou à la fin
// si omis/hors bornes). `parentId === ROOT_PARENT_ID` (ou null/undefined)
// insère au niveau racine de l'arbre plutôt que dans un conteneur.
function insertNodeAt(nodes, parentId, node, index) {
  if (parentId == null || parentId === ROOT_PARENT_ID) {
    const next = [...nodes];
    const at = index == null || index < 0 || index > next.length ? next.length : index;
    next.splice(at, 0, node);
    return next;
  }
  return nodes.map((n) => {
    if (n.id === parentId) {
      const children = Array.isArray(n.children) ? [...n.children] : [];
      const at = index == null || index < 0 || index > children.length ? children.length : index;
      children.splice(at, 0, node);
      return { ...n, children };
    }
    if (Array.isArray(n.children) && n.children.length) {
      return { ...n, children: insertNodeAt(n.children, parentId, node, index) };
    }
    return n;
  });
}

// Déplace un nœud existant vers un autre parent (reparenting), en préservant
// le nœud lui-même (id, style, children…) — seul son emplacement dans
// l'arbre change. Refuse silencieusement (renvoie l'arbre inchangé) si :
//  - le nœud n'existe pas ;
//  - la cible est le nœud lui-même ou l'un de ses propres descendants
//    (empêche toute structure circulaire A → B → A).
export function moveNodeToParent(nodes, nodeId, newParentId, index) {
  if (nodeId === newParentId) return nodes;
  if (newParentId != null && newParentId !== ROOT_PARENT_ID && isDescendant(nodes, nodeId, newParentId)) {
    return nodes;
  }
  const { nodes: withoutNode, removed } = extractNode(nodes, nodeId);
  if (!removed) return nodes;
  return insertNodeAt(withoutNode, newParentId, removed, index);
}

// Profondeur d'un nœud dans l'arbre (0 = niveau racine). -1 si introuvable.
export function getNodeDepth(nodes, id, depth = 0) {
  for (const n of nodes) {
    if (n.id === id) return depth;
    if (Array.isArray(n.children) && n.children.length) {
      const found = getNodeDepth(n.children, id, depth + 1);
      if (found !== -1) return found;
    }
  }
  return -1;
}

// Nombre total de nœuds dans l'arbre (racine + tous les descendants).
export function countNodes(nodes) {
  let count = 0;
  const walk = (list) => {
    for (const n of list || []) {
      count += 1;
      if (Array.isArray(n.children)) walk(n.children);
    }
  };
  walk(nodes);
  return count;
}

// Profondeur maximale de l'arbre (1 = uniquement des nœuds racine).
export function getMaxDepth(nodes, depth = 1) {
  let max = depth;
  for (const n of nodes || []) {
    if (Array.isArray(n.children) && n.children.length) {
      max = Math.max(max, getMaxDepth(n.children, depth + 1));
    }
  }
  return max;
}
